import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const INTERESTS = ["Youth Fellowship", "Educator Training", "Volunteer Locally", "Partnership / Sponsor", "Media & Storytelling", "Research"];

export default function JoinMovementDialog({ project, onClose }) {
    const [form, setForm] = useState({ name: "", email: "", interest: INTERESTS[0], note: "" });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (project) {
            setForm({ name: "", email: "", interest: INTERESTS[0], note: "" });
            setDone(false);
        }
    }, [project]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        if (project) document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [project, onClose]);

    const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!project) return;
        setSubmitting(true);
        try {
            await api.post("/movement/join", {
                project_slug: project.slug,
                region: project.region,
                name: form.name,
                email: form.email,
                interest: form.interest,
                note: form.note,
            });
            setDone(true);
            toast.success(`You're in — welcome to the ${project.region} chapter`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not submit. Try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AnimatePresence>
            {project && (
                <motion.div
                    key="join-overlay"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="fixed inset-0 z-[80] bg-[rgba(6,17,36,0.75)] backdrop-blur-md flex items-center justify-center p-4"
                    onClick={onClose}
                    data-testid="join-dialog-overlay"
                >
                    <motion.div
                        onClick={(e) => e.stopPropagation()}
                        initial={{ opacity: 0, y: 30, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.98 }}
                        transition={{ duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
                        className="w-full max-w-lg bg-[var(--navy)] border border-[var(--gold)] max-h-[90vh] overflow-y-auto"
                        role="dialog"
                        aria-modal="true"
                        data-testid="join-dialog"
                    >
                        <div className="hairline-b p-6 md:p-8 flex items-start justify-between">
                            <div>
                                <div className="label-mono text-[var(--gold)]">— JOIN THE MOVEMENT</div>
                                <h3 className="serif-display text-2xl md:text-3xl mt-3 leading-tight">
                                    {project.region}<span className="text-[var(--gold)]">.</span>
                                </h3>
                                <p className="text-sm text-[var(--ivory)]/70 mt-2">{project.name}</p>
                            </div>
                            <button
                                aria-label="Close"
                                onClick={onClose}
                                className="text-[var(--ivory)]/60 hover:text-[var(--ivory)] transition-colors"
                                data-testid="join-dialog-close"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {done ? (
                            <div className="p-8 md:p-10 text-center" data-testid="join-success">
                                <div className="w-14 h-14 mx-auto flex items-center justify-center border border-[var(--gold)] rounded-full">
                                    <ShieldCheck className="w-6 h-6 text-[var(--gold)]" />
                                </div>
                                <h4 className="serif-display text-2xl md:text-3xl mt-6">
                                    Welcome, {form.name.split(" ")[0]}.
                                </h4>
                                <p className="mt-3 text-[var(--ivory)]/80">
                                    Someone from the {project.region} chapter will reach out with the next step within 3 business days.
                                </p>
                                <button className="btn-ghost mt-8" onClick={onClose} data-testid="join-success-close">
                                    Return to map
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="p-6 md:p-8 space-y-6" data-testid="join-form">
                                <div>
                                    <label className="label-mono block mb-2">Name</label>
                                    <input required className="input-editorial" value={form.name} onChange={change("name")} data-testid="join-name" />
                                </div>
                                <div>
                                    <label className="label-mono block mb-2">Email</label>
                                    <input required type="email" className="input-editorial" value={form.email} onChange={change("email")} data-testid="join-email" />
                                </div>
                                <div>
                                    <label className="label-mono block mb-2">How would you like to get involved?</label>
                                    <select className="input-editorial" value={form.interest} onChange={change("interest")} data-testid="join-interest">
                                        {INTERESTS.map((i) => <option key={i} value={i} className="bg-[var(--navy-deep)]">{i}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="label-mono block mb-2">A short note (optional)</label>
                                    <textarea rows={3} className="input-editorial resize-none" value={form.note} onChange={change("note")} placeholder="Tell us why this region matters to you…" data-testid="join-note" />
                                </div>
                                <button type="submit" disabled={submitting} className="btn-gold w-full justify-center" data-testid="join-submit">
                                    {submitting ? "Sending…" : (<>Count me in <ArrowUpRight className="w-4 h-4" /></>)}
                                </button>
                                <p className="text-xs text-[var(--ivory)]/50 label-mono">
                                    No spam. One email when your chapter next convenes.
                                </p>
                            </form>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
